import { ReactNode, useState, useEffect, useRef, useMemo } from 'react'
import GlobalSearch from './GlobalSearch'
import VerbalePopup from './VerbalePopup'
import RichiestaDocPopup from './RichiestaDocPopup'
import { NotificheBonifici } from './NotificheBonifici'
import { RichiestaRiordinoPopup } from './RichiestaRiordinoPopup'
import Setup2FaPopup from './Setup2FaPopup'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { dashboardApi, magazzinoApi2, menuPermissionsApi, whatsappApi } from '../services/api'
import {
  LayoutDashboard, ClipboardList, Building2, Shield,
  Package, Receipt, LogOut, Menu, X,
  Bell, BarChart3, ChevronRight, Settings, FileText, Users, Calendar, CalendarCheck, CalendarClock, ListChecks, LayoutGrid, Map,
  AlertTriangle, PackageCheck, Clock,
  Briefcase, CreditCard, TrendingUp, SlidersHorizontal, ArrowLeftRight, Car, BarChart2, Truck,
  MessageCircle, Home, FileEdit, Vote, Calculator, FileSpreadsheet, FileCheck,
  Barcode,
} from 'lucide-react'
import clsx from 'clsx'

interface NavItem {
  label: string
  icon: React.ElementType
  path: string
  badge?: number
  roles?: string[]
}

const ADMIN_ROLES = ['superadmin', 'admin', 'readonly']
const STAFF_ROLES = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico', 'readonly']
const TECH_ROLES  = [...STAFF_ROLES, 'tecnico_senior', 'tecnico']

const DELTA_ITEMS: NavItem[] = [
  { label: 'Dashboard',        icon: LayoutDashboard, path: '/dashboard',   roles: ['superadmin','admin','dispatcher','responsabile_tecnico','contabilita','readonly'] },
  { label: 'Pratiche',         icon: ClipboardList,   path: '/pratiche' },
  { label: 'Calendario',       icon: Calendar,        path: '/calendario' },
  { label: 'Mappa',            icon: Map,             path: '/mappa' },
  { label: 'Magazzino',        icon: Package,         path: '/magazzino',   roles: TECH_ROLES },
  { label: 'Rapportini',       icon: FileText,        path: '/rapportini' },
  { label: 'Report Ore Tecnici', icon: Clock,         path: '/report-ore-tecnici', roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'contabilita', 'readonly'] },
  { label: 'Produzione',       icon: Receipt,         path: '/fatturazione', roles: ['admin', 'superadmin', 'contabilita', 'responsabile_tecnico', 'readonly'] },
  { label: 'Dispatcher Board', icon: LayoutGrid,      path: '/dispatcher',  roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'readonly'] },
]

const REST_ITEMS: NavItem[] = [
  { label: 'Il Mio Turno',     icon: CalendarCheck,   path: '/mio-turno',   roles: ['tecnico', 'tecnico_senior'] },
  { label: 'Report',           icon: BarChart3,        path: '/report',      roles: [...STAFF_ROLES, 'contabilita'] },
  { label: 'Report Economico', icon: TrendingUp,       path: '/report-economico', roles: ['admin', 'superadmin', 'contabilita', 'responsabile_tecnico'] },
  { label: 'WhatsApp',         icon: MessageCircle,    path: '/whatsapp',    roles: STAFF_ROLES },
  { label: 'Cambio Porte',     icon: ArrowLeftRight,   path: '/cambio-porte' },
]

const NAV_ITEMS = [...DELTA_ITEMS, ...REST_ITEMS]

const CONF_CHILDREN: NavItem[] = [
  { label: 'Impostazioni',    icon: SlidersHorizontal, path: '/impostazioni',   roles: ['superadmin', 'admin'] },
  { label: 'Utenti',          icon: Settings,           path: '/utenti',         roles: ADMIN_ROLES },
  { label: 'Committenti',     icon: Building2,           path: '/committenti',    roles: STAFF_ROLES },
  { label: 'Clienti Finali',  icon: Users,               path: '/clienti',        roles: STAFF_ROLES },
  { label: 'Checklist',          icon: ListChecks,     path: '/checklist',              roles: ['admin', 'superadmin', 'dispatcher', 'readonly'] },
  { label: 'Orari Apertura',     icon: Clock,          path: '/orari-apertura',         roles: ['admin', 'superadmin', 'dispatcher', 'readonly'] },
  { label: 'Template Rapportini', icon: FileEdit, path: '/rapportino-templates', roles: ['admin', 'superadmin'] },
  { label: 'RLS Votazione',      icon: Vote,           path: '/rlsvote-crea-elezione',  roles: ['superadmin', 'admin'] },
]
const CONF_PATHS  = CONF_CHILDREN.map(c => c.path)
const DELTA_PATHS = DELTA_ITEMS.map(c => c.path)

const CAB_CHILDREN: NavItem[] = [
  { label: 'Nuova Etichetta', icon: Barcode,        path: '/cab',             roles: [...TECH_ROLES, 'contabilita'] },
  { label: 'Archivio',        icon: ClipboardList,  path: '/cab/archivio',    roles: [...TECH_ROLES, 'contabilita'] },
  { label: 'Anagrafica NMU',  icon: FileSpreadsheet, path: '/cab/anagrafica', roles: ADMIN_ROLES },
  { label: 'Stampanti',       icon: Settings,        path: '/cab/stampanti',  roles: ADMIN_ROLES },
]
const CAB_PATHS = CAB_CHILDREN.map(c => c.path)

const IMECO_CHILDREN: NavItem[] = [
  { label: 'Assegnazioni', icon: Users,     path: '/calendario-assegnazioni', roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'tecnico', 'tecnico_senior'] },
  { label: 'Commesse',     icon: Briefcase, path: '/commesse',                roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'contabilita'] },
]
const IMECO_PATHS = IMECO_CHILDREN.map(c => c.path)

const TRASFERTE_CHILDREN: NavItem[] = [
  { label: 'Trasferte',        icon: Car,         path: '/trasferte' },
  { label: 'Spese & Rimborsi', icon: CreditCard,  path: '/spese-rimborso',    roles: ['admin', 'superadmin', 'responsabile_tecnico', 'contabilita', 'tecnico', 'tecnico_senior'] },
  { label: 'Report Trasferte', icon: BarChart2,   path: '/trasferte/report',  roles: ['admin', 'superadmin', 'dispatcher', 'responsabile_tecnico', 'contabilita', 'readonly'] },
  { label: 'Gest. Personale',  icon: Users,       path: '/gestione-personale', roles: STAFF_ROLES },
  { label: 'Commesse',         icon: Briefcase,   path: '/impostazioni?tab=commesse', roles: ['admin', 'superadmin'] },
]
const TRASFERTE_PATHS = TRASFERTE_CHILDREN.map(c => c.path)

const FLEET_CHILDREN: NavItem[] = [
  { label: 'Parco Auto', icon: Truck, path: '/fleet', roles: STAFF_ROLES },
]
const FLEET_PATHS = FLEET_CHILDREN.map(c => c.path)

const RED_CHILDREN: NavItem[] = [
  { label: 'Dashboard RED',      icon: BarChart3, path: '/red/dashboard', roles: STAFF_ROLES },
  { label: 'Portfolio Immobili', icon: Building2, path: '/red',           roles: STAFF_ROLES },
  { label: 'Mappa Immobili',     icon: Map,       path: '/red/mappa',     roles: STAFF_ROLES },
  { label: 'Cronoprogramma',     icon: Calendar,  path: '/red/gantt',     roles: STAFF_ROLES },
  { label: 'Pipeline Opportunità', icon: Briefcase, path: '/red/opportunita', roles: STAFF_ROLES },
  { label: 'Rubrica Professionisti', icon: Users, path: '/red/professionisti', roles: STAFF_ROLES },
  { label: 'Pratiche USR', icon: FileText, path: '/red/pratiche-usr', roles: STAFF_ROLES },
  { label: 'Agenda', icon: CalendarClock, path: '/red/agenda', roles: STAFF_ROLES },
  { label: 'Impostazioni RED', icon: Settings, path: '/red/impostazioni', roles: STAFF_ROLES },
]
const RED_PATHS = RED_CHILDREN.map(c => c.path)

const NICI_CHILDREN: NavItem[] = [
  { label: 'Dashboard NICI',       icon: LayoutDashboard,  path: '/nici',         roles: STAFF_ROLES },
  { label: 'Pratiche Sopralluogo', icon: ClipboardList,    path: '/timsurvey',    roles: [...STAFF_ROLES, 'tecnico_senior', 'tecnico'] },
  { label: 'Offerte / Computi',    icon: Calculator,       path: '/nici/offerte', roles: [...STAFF_ROLES, 'contabilita'] },
  { label: 'Listini',              icon: FileSpreadsheet,  path: '/nici/listini', roles: STAFF_ROLES },
]
const NICI_PATHS = NICI_CHILDREN.map(c => c.path)

const RFI_CHILDREN: NavItem[] = [
  { label: 'Ordini di Lavoro', icon: FileText,  path: '/rfi',              roles: STAFF_ROLES },
  { label: 'Rapportini',       icon: FileCheck, path: '/rfi/rapportini',   roles: STAFF_ROLES },
  { label: 'Listino Prezzi',   icon: FileText,  path: '/rfi/listino',      roles: STAFF_ROLES },
  { label: 'Impostazioni RFI', icon: Settings,  path: '/rfi/impostazioni', roles: STAFF_ROLES },
]
const RFI_PATHS = RFI_CHILDREN.map(c => c.path)

/**
 * Elenco completo di tutte le voci di menu gestibili dal sistema permessi granulari
 * (`user_menu_permissions` / `GET /api/menu-permissions/my-menus`).
 * Esportato per essere riutilizzato da `App.tsx` (`ProtectedRoute`) in modo che il
 * controllo dei permessi non sia applicato solo alla sidebar ma anche alla rotta
 * effettiva — evita che un link diretto (es. click su un frazionario) bypassi
 * un menu disabilitato per l'utente. Ordinato per lunghezza di path decrescente
 * così il match su rotte con parametro (es. `/pratiche/:id`) trova il prefisso
 * più specifico.
 */
export const ALL_NAV_ITEMS: NavItem[] = [
  ...DELTA_ITEMS,
  ...CAB_CHILDREN,
  ...REST_ITEMS,
  ...CONF_CHILDREN,
  ...IMECO_CHILDREN,
  ...TRASFERTE_CHILDREN,
  ...FLEET_CHILDREN,
  ...RED_CHILDREN,
  ...RFI_CHILDREN,
  ...NICI_CHILDREN,
].sort((a, b) => b.path.length - a.path.length)

/* ── Nav item ──────────────────────────────────────────── */
function NavLink({ item, active, onClick }: {
  item: NavItem; active: boolean; onClick: () => void
}) {
  const Icon = item.icon
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className="flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
      style={{
        background: active ? 'rgba(255,255,255,0.18)' : 'transparent',
        color: active ? '#ffffff' : 'rgba(255,255,255,0.65)',
        fontWeight: active ? 600 : 500,
        letterSpacing: '-0.01em',
      }}
      onMouseEnter={e => {
        if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'
        if (!active) (e.currentTarget as HTMLElement).style.color = '#ffffff'
      }}
      onMouseLeave={e => {
        if (!active) (e.currentTarget as HTMLElement).style.background = 'transparent'
        if (!active) (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)'
      }}
    >
      <Icon
        size={15}
        strokeWidth={active ? 2.2 : 1.8}
        style={{ flexShrink: 0, opacity: active ? 1 : 0.75 }}
      />
      <span className="flex-1">{item.label}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <span style={{
          background: active ? 'rgba(255,255,255,0.25)' : 'var(--mac-orange)',
          color: 'white',
          fontSize: '10px',
          fontWeight: 700,
          borderRadius: 99,
          padding: '1px 6px',
          minWidth: 18,
          textAlign: 'center' as const,
          lineHeight: 1.6,
        }}>
          {item.badge}
        </span>
      )}
    </Link>
  )
}

/* ── Layout ────────────────────────────────────────────── */
export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth()
  const location         = useLocation()
  const navigate         = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showNotifiche, setShowNotifiche] = useState(false)
  const [deltaOpen,  setDeltaOpen]  = useState(() => !CAB_PATHS.some(p => location.pathname.startsWith(p)) && !IMECO_PATHS.some(p => location.pathname.startsWith(p)) && !TRASFERTE_PATHS.some(p => location.pathname.startsWith(p)) && !FLEET_PATHS.some(p => location.pathname.startsWith(p)) && !RED_PATHS.some(p => location.pathname.startsWith(p)) && !RFI_PATHS.some(p => location.pathname.startsWith(p)))
  const [configOpen, setConfigOpen] = useState(() => CONF_PATHS.some(p => location.pathname.startsWith(p)))
  const [cabOpen,    setCabOpen]    = useState(() => CAB_PATHS.some(p => location.pathname.startsWith(p)))
  const [imecOpen,   setImecOpen]   = useState(() => IMECO_PATHS.some(p => location.pathname.startsWith(p)))
  const [trasferteOpen, setTrasferteOpen] = useState(() => TRASFERTE_PATHS.some(p => location.pathname.startsWith(p)))
  const [fleetOpen,  setFleetOpen]  = useState(() => FLEET_PATHS.some(p => location.pathname.startsWith(p)))
  const [redOpen,    setRedOpen]    = useState(() => RED_PATHS.some(p => location.pathname.startsWith(p)))
  const [rfiOpen,    setRfiOpen]    = useState(() => RFI_PATHS.some(p => location.pathname.startsWith(p)))
  const [niciOpen,   setNiciOpen]   = useState(() => NICI_PATHS.some(p => location.pathname.startsWith(p)))
  const [dropPos, setDropPos] = useState({ top: 48, right: 16 })
  const bellRef = useRef<HTMLButtonElement>(null)
  const dropRef = useRef<HTMLDivElement>(null)

  const { data: slaAlert } = useQuery({
    queryKey: ['sla-alert-badge'],
    queryFn: () => dashboardApi.slaAlert().then(r => r.data),
    refetchInterval: 300_000,
    staleTime: 60_000,
  })

  const { data: myMenus } = useQuery({
    queryKey: ['my-menus', user?.id],
    queryFn: () => menuPermissionsApi.myMenus().then(r => r.data as { id: string; route: string }[]),
    enabled: !!user,
    staleTime: 60_000,
  })

  const menuChildVisible = (c: NavItem) =>
    myMenus && myMenus.length > 0
      ? myMenus.some(m => m.route === c.path)
      : (!c.roles || c.roles.includes(user?.role ?? ''))

  const isTecnico  = ['tecnico', 'tecnico_senior'].includes(user?.role ?? '')
  const isStaff    = ['superadmin', 'admin', 'dispatcher', 'responsabile_tecnico'].includes(user?.role ?? '')
  const isReadonly = user?.role === 'readonly'

  const { data: trasferimenti } = useQuery({
    queryKey: isTecnico ? ['trasf-in-arrivo-badge'] : ['trasf-verso-centrale-badge'],
    queryFn: isTecnico
      ? () => magazzinoApi2.trasferimentiInArrivo().then(r => r.data)
      : () => magazzinoApi2.trasferimentiVersoCentrale().then(r => r.data),
    enabled: isTecnico || isStaff,
    refetchInterval: 120_000,
    staleTime: 30_000,
  })

  const { data: waContacts } = useQuery({
    queryKey: ['wa-contacts-badge'],
    queryFn: () => whatsappApi.contacts().then(r => r.data as { unread_count: number }[]),
    enabled: isStaff,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })

  const slaCount: number    = (slaAlert as any)?.count ?? 0
  const trasfCount: number  = Array.isArray(trasferimenti) ? trasferimenti.length : 0
  const waUnread: number    = Array.isArray(waContacts) ? waContacts.reduce((s, c) => s + (c.unread_count || 0), 0) : 0
  const totalBadge: number  = slaCount + trasfCount

  // chiudi dropdown se click fuori
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        bellRef.current && !bellRef.current.contains(e.target as Node) &&
        dropRef.current && !dropRef.current.contains(e.target as Node)
      ) {
        setShowNotifiche(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleLogout = () => { logout(); navigate('/login') }

  const currentPage = [...DELTA_ITEMS, ...CAB_CHILDREN, ...REST_ITEMS, ...CONF_CHILDREN, ...IMECO_CHILDREN, ...TRASFERTE_CHILDREN, ...FLEET_CHILDREN, ...RED_CHILDREN, ...NICI_CHILDREN, ...RFI_CHILDREN].find(n =>
    n.path === location.pathname ||
    (n.path !== '/' && location.pathname.startsWith(n.path))
  )

  const initials = `${user?.nome?.[0] ?? ''}${user?.cognome?.[0] ?? ''}`

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--mac-bg)' }}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 lg:hidden"
          style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', cursor: 'pointer' }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ──────────────────────────────────── */}
      <aside
        className={clsx(
          'fixed inset-y-0 left-0 z-30 w-52 flex flex-col',
          'transition-transform duration-300 lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{
          background: '#1E40AF',
          borderRight: '1px solid rgba(0,0,0,0.15)',
        }}
      >

        {/* Logo / brand */}
        <div className="flex flex-col px-4 pt-5 pb-3.5 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>

          <div className="flex items-center gap-2.5 mb-2.5">
            {/* X-Sophia 360 icon */}
            <img
              src="/favicon-xsophia.png"
              alt="X-Sophia 360"
              style={{
                height: 26,
                width: 26,
                objectFit: 'contain',
                borderRadius: 6,
                opacity: 0.96,
                flexShrink: 0,
              }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
            <div className="min-w-0">
              <div style={{
                fontFamily: "'Figtree', sans-serif",
                fontWeight: 800,
                fontSize: '1.05rem',
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}>
                XSophia
              </div>
              <div style={{
                fontSize: '9.5px',
                color: 'rgba(255,255,255,0.55)',
                marginTop: 2,
                letterSpacing: '0.06em',
                fontWeight: 600,
                textTransform: 'uppercase' as const,
              }}>
                X-Sophia 360
              </div>
            </div>

            {/* Close button (mobile) */}
            <button
              className="lg:hidden ml-auto p-1.5 rounded-md"
              style={{ color: 'var(--mac-label-3)' }}
              onClick={() => setSidebarOpen(false)}
            >
              <X size={14} />
            </button>
          </div>

          {/* Slogan */}
          <p style={{
            fontSize: '10.5px',
            color: 'rgba(255,255,255,0.45)',
            lineHeight: 1.5,
            fontStyle: 'italic',
            letterSpacing: '0.01em',
          }}>
            Field operations? Don't worry,{' '}
            everything is under your control with Sophia.
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-1 space-y-[2px] overflow-y-auto">

          {/* Gruppo Delta Poste */}
          <div>
            <button
              onClick={() => setDeltaOpen(o => !o)}
              className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
              style={{
                background: deltaOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                color: deltaOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
              }}
              onMouseEnter={e => { if (!deltaOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
              onMouseLeave={e => { if (!deltaOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
            >
              <ClipboardList size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
              <span className="flex-1 text-left">Delta Poste</span>
              <ChevronRight size={13} style={{ opacity: 0.5, transform: deltaOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
            {deltaOpen && (
              <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                {DELTA_ITEMS
                  .filter(item => {
                    const roleOk = !item.roles || item.roles.includes(user?.role ?? '')
                    if (myMenus && myMenus.length > 0) return myMenus.some(m => m.route === item.path) && roleOk
                    return roleOk
                  })
                  .map(item => {
                    const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
                    return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                  })}
              </div>
            )}
          </div>

          {/* Gruppo ETICHETTE CAB */}
          {CAB_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setCabOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: cabOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: cabOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!cabOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!cabOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <Barcode size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">ETICHETTE CAB</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: cabOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {cabOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {CAB_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Gruppo IMECO */}
          {IMECO_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setImecOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: imecOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: imecOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!imecOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!imecOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <Briefcase size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">IMECO</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: imecOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {imecOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {IMECO_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Gruppo Trasferte */}
          {TRASFERTE_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setTrasferteOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: trasferteOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: trasferteOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!trasferteOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!trasferteOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <Car size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">Trasferte</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: trasferteOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {trasferteOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {TRASFERTE_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Gruppo Fleet */}
          {FLEET_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setFleetOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: fleetOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: fleetOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!fleetOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!fleetOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <Truck size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">Fleet</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: fleetOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {fleetOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {FLEET_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Gruppo RED — Ricostruzione */}
          {RED_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setRedOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: redOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: redOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!redOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!redOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <Building2 size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">RED — Ricostruzione</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: redOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {redOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {RED_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Gruppo NICI */}
          {NICI_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setNiciOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: niciOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: niciOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!niciOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!niciOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <Calculator size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">NICI</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: niciOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {niciOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {NICI_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Gruppo RFI */}
          {RFI_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setRfiOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: rfiOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: rfiOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!rfiOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!rfiOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <FileText size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">RFI</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: rfiOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {rfiOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {RFI_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}

          {/* Separatore */}
          <div style={{ height: 1, background: 'rgba(255,255,255,0.10)', margin: '6px 4px' }} />

          {/* Voci restanti */}
          {REST_ITEMS
            .filter(item => {
              const roleOk = !item.roles || item.roles.includes(user?.role ?? '')
              if (myMenus && myMenus.length > 0) return myMenus.some(m => m.route === item.path) && roleOk
              return roleOk
            })
            .map(item => {
              const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path))
              const badge = item.path === '/whatsapp' && waUnread > 0 ? waUnread : item.badge
              return <NavLink key={item.path} item={{ ...item, badge }} active={active} onClick={() => setSidebarOpen(false)} />
            })}

          {/* Gruppo Configurazione */}
          {CONF_CHILDREN.some(menuChildVisible) && (
            <div>
              <button
                onClick={() => setConfigOpen(o => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13.5px] transition-all duration-100 select-none outline-none"
                style={{
                  background: configOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                  color: configOpen ? '#ffffff' : 'rgba(255,255,255,0.65)',
                  fontWeight: 500, letterSpacing: '-0.01em', cursor: 'pointer', border: 'none',
                }}
                onMouseEnter={e => { if (!configOpen) { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.10)'; (e.currentTarget as HTMLElement).style.color = '#ffffff' } }}
                onMouseLeave={e => { if (!configOpen) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.65)' } }}
              >
                <SlidersHorizontal size={15} strokeWidth={1.8} style={{ flexShrink: 0, opacity: 0.75 }} />
                <span className="flex-1 text-left">Configurazione</span>
                <ChevronRight size={13} style={{ opacity: 0.5, transform: configOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
              {configOpen && (
                <div className="ml-3 mt-[2px] space-y-[2px] border-l border-white/10 pl-2">
                  {CONF_CHILDREN
                    .filter(menuChildVisible)
                    .map(item => {
                      const active = location.pathname === item.path || location.pathname.startsWith(item.path)
                      return <NavLink key={item.path} item={item} active={active} onClick={() => setSidebarOpen(false)} />
                    })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Banner sola lettura */}
        {isReadonly && (
          <div style={{
            margin: '0 8px 6px',
            background: 'rgba(239,68,68,0.18)',
            border: '1px solid rgba(239,68,68,0.35)',
            borderRadius: 8,
            padding: '5px 10px',
            fontSize: 10,
            fontWeight: 700,
            color: '#fca5a5',
            letterSpacing: '0.08em',
            textAlign: 'center',
            textTransform: 'uppercase',
          }}>
            🔒 Sola lettura
          </div>
        )}

        {/* User footer */}
        <div className="px-2 py-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }}>
          <div
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px]"
            style={{ background: 'rgba(255,255,255,0.10)' }}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.25)', fontSize: '11px' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#ffffff', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.nome} {user?.cognome}
              </div>
              <div style={{ fontSize: '10.5px', color: 'rgba(255,255,255,0.50)', textTransform: 'capitalize' as const, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {user?.role?.replace(/_/g, ' ')}
              </div>
            </div>
            <button
              onClick={() => navigate('/profilo')}
              className="p-1.5 rounded-md transition-colors flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.50)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#60a5fa')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.50)')}
              title="Sicurezza account"
            >
              <Shield size={13} />
            </button>
            <button
              onClick={handleLogout}
              className="p-1.5 rounded-md transition-colors flex-shrink-0"
              style={{ color: 'rgba(255,255,255,0.50)', background: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#ff6b6b')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.50)')}
              title="Logout"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main area ──────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* Topbar */}
        <header
          className="flex items-center gap-3 px-4 lg:px-5 flex-shrink-0"
          style={{
            height: 44,
            background: '#ffffff',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-1.5 rounded-[8px] transition-colors"
            style={{ color: 'var(--mac-label-2)', background: 'transparent', border: 'none', cursor: 'pointer' }}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={18} />
          </button>

          {/* Back to home — visibile per tutti i ruoli non-tecnico quando non si è sulla home */}
          {!isTecnico && location.pathname !== '/' && (
            <Link
              to="/"
              className="flex items-center gap-1.5 flex-shrink-0"
              style={{
                padding: '4px 10px',
                borderRadius: 8,
                background: '#1E40AF',
                color: 'white',
                textDecoration: 'none',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#1d3a9e')}
              onMouseLeave={e => (e.currentTarget.style.background = '#1E40AF')}
            >
              <Home size={12} />
              <span>Home</span>
            </Link>
          )}

          {/* Breadcrumb */}
          <div className="flex-1 hidden sm:flex items-center gap-1.5 min-w-0">
            <span style={{
              fontSize: '11.5px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase' as const,
              color: 'var(--mac-label-3)',
              flexShrink: 0,
            }}>
              XSophia
            </span>
            <ChevronRight size={12} style={{ color: 'var(--mac-label-4)', flexShrink: 0 }} />
            <span style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--mac-label)',
              letterSpacing: '-0.015em',
              whiteSpace: 'nowrap' as const,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {currentPage?.label ?? 'Dashboard'}
            </span>
          </div>

          {/* Live indicator */}
          <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
            <span
              style={{
                display: 'inline-block',
                width: 6, height: 6,
                borderRadius: '50%',
                background: 'var(--mac-green)',
                boxShadow: '0 0 5px rgba(48,209,88,0.7)',
                animation: 'pulse-dot 2s ease-in-out infinite',
              }}
            />
            <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--mac-label-3)' }}>
              Live
            </span>
          </div>

          {/* Global Search */}
          <div className="hidden sm:block">
            <GlobalSearch />
          </div>

          {/* Bell */}
          <div style={{ position: 'relative', flexShrink: 0, marginLeft: 'auto' }}>
            <button
              ref={bellRef}
              onClick={() => {
                if (!showNotifiche && bellRef.current) {
                  const r = bellRef.current.getBoundingClientRect()
                  setDropPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) })
                }
                setShowNotifiche(v => !v)
              }}
              title={totalBadge > 0 ? `${totalBadge} notifiche` : 'Nessuna notifica'}
              className="relative rounded-[8px] flex-shrink-0"
              style={{
                color: 'var(--mac-label-2)', background: showNotifiche ? 'rgba(0,0,0,0.05)' : 'transparent',
                border: 'none', cursor: 'pointer', transition: 'background 0.15s',
                padding: '10px', minWidth: 40, minHeight: 40,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.05)')}
              onMouseLeave={e => { if (!showNotifiche) e.currentTarget.style.background = 'transparent' }}
            >
              <Bell size={18} />
              {totalBadge > 0 ? (
                <span style={{
                  position: 'absolute', top: 4, right: 4,
                  background: '#ef4444', color: '#fff',
                  fontSize: 9, fontWeight: 700,
                  borderRadius: 99, padding: '1px 4px',
                  minWidth: 15, textAlign: 'center',
                  lineHeight: '13px', pointerEvents: 'none',
                }}>
                  {totalBadge}
                </span>
              ) : (
                <span style={{
                  position: 'absolute', top: 7, right: 7,
                  width: 6, height: 6, borderRadius: '50%',
                  background: 'var(--mac-blue)',
                  boxShadow: '0 0 4px rgba(10,132,255,0.8)',
                  pointerEvents: 'none',
                }} />
              )}
            </button>

            {/* Dropdown notifiche — position:fixed per evitare clip da overflow:hidden */}
            {showNotifiche && (
              <div
                ref={dropRef}
                style={{
                  position: 'fixed', top: dropPos.top, right: dropPos.right, zIndex: 9999,
                  background: '#fff', borderRadius: 12,
                  width: 300, maxWidth: 'calc(100vw - 24px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>Notifiche</span>
                  {totalBadge > 0 && (
                    <span style={{ fontSize: 11, background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>
                      {totalBadge} in attesa
                    </span>
                  )}
                </div>

                {slaCount > 0 && (
                  <div style={{ padding: '10px 16px', borderBottom: trasfCount > 0 ? '1px solid #f3f4f6' : undefined }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#92400e', margin: 0 }}>
                          {slaCount} WO con SLA in scadenza nelle prossime 24h
                        </p>
                        <button
                          onClick={() => { setShowNotifiche(false); navigate('/pratiche') }}
                          style={{ marginTop: 6, fontSize: 12, color: '#1E40AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                        >
                          Vai alle Pratiche →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {trasfCount > 0 && (
                  <div style={{ padding: '10px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <PackageCheck size={16} style={{ color: '#F97316', flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, color: '#92400e', margin: 0 }}>
                          {isTecnico
                            ? `${trasfCount} trasferiment${trasfCount === 1 ? 'o' : 'i'} in arrivo da confermare`
                            : `${trasfCount} restituzion${trasfCount === 1 ? 'e' : 'i'} a magazzino in attesa`
                          }
                        </p>
                        <button
                          onClick={() => { setShowNotifiche(false); navigate('/magazzino') }}
                          style={{ marginTop: 6, fontSize: 12, color: '#1E40AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}
                        >
                          Vai al Magazzino →
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {totalBadge === 0 && (
                  <div style={{ padding: '20px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    ✓ Nessuna notifica attiva
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="hidden sm:block" style={{ width: 1, height: 16, background: 'var(--mac-sep)', flexShrink: 0 }} />

          <span
            className="hidden sm:block flex-shrink-0"
            style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--mac-label-2)' }}
          >
            {user?.nome} {user?.cognome}
          </span>
        </header>

        {/* Page content */}
        <main
          className={
            location.pathname === '/mappa'
              ? 'flex-1 overflow-hidden'
              : 'flex-1 overflow-y-auto p-4 lg:p-6'
          }
          style={{ background: 'var(--mac-bg)' }}
        >
          {children}
        </main>
      </div>

      {/* Popup verbale pendente per tecnici */}
      <VerbalePopup />
      {/* Popup richiesta documento da admin */}
      <RichiestaDocPopup />
      {/* Notifiche conferma bonifici trasferte */}
      <NotificheBonifici />
      <RichiestaRiordinoPopup />
      <Setup2FaPopup />
    </div>
  )
}
